import { formatQuantity } from '../utils/quantityFormat';

/**
 * Confezionamento per riga prodotto dell'offerta (richiesta Say Farm): come va
 * suddivisa la quantità della riga, es. 5 kg = 5 confezioni da 1 kg.
 *
 * Sta in deal_services.metadata.packaging_split, come la nota di lavorazione
 * (lineProcessingNotes.ts): nessuna migration, il magazzino lo legge
 * dall'offerta attraverso l'ordine (fetchOrderProcessingNotes) e la stampa
 * dell'ordine lo riporta sotto l'articolo.
 *
 * Funzione per organizzazione: settings.warehouse_settings.line_packaging_split_enabled.
 * `size` è nell'unità della riga (deal_services.unit_of_measure).
 */

export const PACKAGING_SPLIT_KEY = 'packaging_split';

export interface PackagingSplit {
  /** Numero di confezioni */
  packs: number;
  /** Quantità per confezione, nell'unità della riga */
  size: number;
}

/** Confezionamento della riga, null se assente o non valido. */
export function packagingSplitOf(metadata: unknown): PackagingSplit | null {
  const v = (metadata as Record<string, unknown> | null | undefined)?.[PACKAGING_SPLIT_KEY] as Partial<PackagingSplit> | undefined;
  const packs = Number(v?.packs);
  const size = Number(v?.size);
  return packs > 0 && size > 0 ? { packs, size } : null;
}

/** Metadata con il confezionamento aggiornato; null = chiave tolta. */
export function withPackagingSplit(metadata: unknown, split: PackagingSplit | null): Record<string, unknown> {
  const next: Record<string, unknown> = { ...((metadata as Record<string, unknown> | null | undefined) || {}) };
  if (split && split.packs > 0 && split.size > 0) next[PACKAGING_SPLIT_KEY] = { packs: split.packs, size: split.size };
  else delete next[PACKAGING_SPLIT_KEY];
  return next;
}

/** «5 confezioni da 1 kg», «1 confezione da 5 kg». */
export function describePackagingSplit(split: PackagingSplit, unit?: string | null): string {
  const packs = formatQuantity(split.packs, { unit: 'pz', unitDisplay: 'none' });
  return `${packs} ${split.packs === 1 ? 'confezione' : 'confezioni'} da ${formatQuantity(split.size, { unit: unit || null })}`;
}

/** «5 × 1 kg», per la stampa (vale in tutte le lingue). */
export function shortPackagingSplit(split: PackagingSplit, unit?: string | null): string {
  return `${formatQuantity(split.packs, { unit: 'pz', unitDisplay: 'none' })} × ${formatQuantity(split.size, { unit: unit || null })}`;
}

/**
 * True se confezioni × quantità non dà la quantità della riga. Tollera
 * l'arrotondamento a 4 decimali della quantità per confezione (1 kg in 3
 * confezioni = 0,3333 kg l'una).
 */
export function packagingMismatch(split: PackagingSplit, quantity: number): boolean {
  return Math.abs(split.packs * split.size - quantity) > split.packs * 0.00005 + 1e-9;
}
